import { AgFilterComponent } from '@ag-grid-community/angular';
import { IDoesFilterPassParams, IRowNode } from '@ag-grid-community/core';
import { ChangeDetectionStrategy, Component, HostBinding, OnDestroy, ViewEncapsulation } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { NgIf } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ActivatedRoute } from '@angular/router';

import { TextFilter, FilterType } from '../../models/ag-grid';
import { DatacenterType } from '../../models/datacenter-aggrid-view';
import { ColDefWithFields } from '../column-type';
import { FilterInstance } from '../../services/datacenter-filter';

import { DatacenterFilterParamsInterface } from './datacenter-date-filter.component';

const SHIPMENT_MULTI_VALUE_TEXT_FILTER_NAME =
	<TextFilter['customFilterName']> 'shipment-multi-value-text-filter';

@Component({
	selector: 'copo-datacenter-text-filter',
	templateUrl: './datacenter-text-filter.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [

		MatFormFieldModule,

		MatInputModule,
		FormsModule,
		ReactiveFormsModule,
		NgIf,

		MatButtonModule,
		MatIconModule
	]
})
export class DatacenterTextFilterComponent implements AgFilterComponent, FilterInstance, OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;

	searchTextControl = new FormControl('');
	header: string | undefined;
	lengthText: string | undefined;

	private isClientSide = false;
	private model?: TextFilter;
	private params?: DatacenterFilterParamsInterface;
	private readonly textControlSubscription: Subscription;

	constructor(private readonly route: ActivatedRoute) {
		this.textControlSubscription = this.searchTextControl.valueChanges.pipe(
			debounceTime(500),
			distinctUntilChanged()
		).subscribe(value => {
			const val = <string | undefined>value;
			this.lengthText = val;
			this.model = !val && !this.model?.searchTexts ? undefined : {
				...this.model,
				customFilterName: this.getCustomFilterName(),
				searchText: val?.trim() ?? undefined
			};
			if (this.params) {
				this.params.filterChangedCallback();
			}
		});
	}

	agInit(params: DatacenterFilterParamsInterface) {
		this.params = params;
		this.header = this.params.colDef.headerName;
		const rowModelType = params.rowModel.getType();
		this.isClientSide = rowModelType === 'clientSide';
	}

	isFilterActive() {
		return this.model?.searchText !== undefined
			|| (this.model?.searchTexts !== undefined && this.model.searchTexts.length > 0);
	}

	doesFilterPass(filterParams: IDoesFilterPassParams<Record<string, unknown>>) {
		if (!this.isClientSide) {
			return this.isFilterActive();
		}
		if (!this.params
			|| typeof filterParams.data !== 'object'
			|| (!this.model?.searchText && !this.model?.searchTexts?.length)) {
			return true;
		}
		const value = this.getColumnValue(filterParams.node);
		if (value === undefined) {
			return false;
		}
		if (this.model.searchTexts?.length && !this.model.searchTexts.some(text => value.includes(text))) {
			return false;
		}
		return this.model.searchText === undefined || value.includes(this.model.searchText);
	}

	getColumnValue(node: IRowNode<Record<string, unknown>>): string | undefined {
		if (!this.params || typeof node.data !== 'object') {
			return undefined;
		}
		const { data } = node;
		if (typeof this.params.colDef.valueGetter === 'function') {
			const value = <unknown> this.params.colDef.valueGetter({
				api: this.params.api,
				colDef: this.params.colDef,
				column: this.params.column,
				context: this.params.context,
				data,
				node,
				getValue(field: string) {
					return data[field];
				}
			});
			// eslint-disable-next-line @typescript-eslint/no-base-to-string
			return value ? String(value) : undefined;
		} else if (this.params.colDef.field) {
			const value = data[this.params.colDef.field];
			// eslint-disable-next-line @typescript-eslint/no-base-to-string
			return value ? String(value) : undefined;
		}
		return undefined;
	}

	getModel(): TextFilter | null {
		if (!this.isFilterActive()) {
			return null;
		}
		if (this.params?.colDef.colId === 'producedLengthDimensionConcat' && this.lengthText) {
			return {
				customFilterName: this.getCustomFilterName(),
				searchText: this.scaleToFixed(this.lengthText, 1000, 6)
			};
		}
		// All other columns are unchanged
		return { ...this.model, customFilterName: this.getCustomFilterName() };
	}

	setModel(textFilter: TextFilter | null) {
		if (!textFilter || (!textFilter.searchText && !textFilter.searchTexts?.length)) {
			this.model = undefined;
			this.searchTextControl.reset();
			return;
		}
		if (textFilter.searchText !== undefined) {
			textFilter.searchText = this.params?.colDef.colId === 'lengthDimensionConcat'
				|| this.params?.colDef.colId === 'producedLengthDimensionConcat'
				? String(Number(textFilter.searchText) / 1000) : textFilter.searchText;
		}
		this.model = {
			...textFilter,
			customFilterName: this.getCustomFilterName()
		};
		this.searchTextControl.setValue(textFilter.searchText ?? null);
	}

	getCustomFilterName(): TextFilter['customFilterName'] {
		if (this.params?.colDef.filterParams && this.params.filterType
			&& this.params.filterType === 'SHIPMENT_MULTI_VALUE_TEXT_FILTER') {
			return SHIPMENT_MULTI_VALUE_TEXT_FILTER_NAME;
		}
		if (this.params?.colDef.filterParams && this.params.filterType
			&& this.params.filterType === 'DOCUMENT_TEXT_FILTER') {
			return FilterType.DOCUMENT_TEXT_FILTER;
		}
		if (this.isShipmentMultiLineColumn()) {
			return SHIPMENT_MULTI_VALUE_TEXT_FILTER_NAME;
		}
		return FilterType.TEXT_FILTER;
	}

	ngOnDestroy() {
		this.textControlSubscription.unsubscribe();
	}

	resetText() {
		this.searchTextControl.setValue(null);
	}

	private scaleToFixed(value: string, scale = 1000, decimals = 6): string {
		const n = Number(String(value).replace(/,/g, '').trim());
		return Number.isFinite(n) ? (n * scale).toFixed(decimals) : value;
	}

	private isShipmentMultiLineColumn() {
		const datacenterType = <DatacenterType | undefined> this.route.snapshot.data['type'];
		const colDef = <ColDefWithFields<Record<string, unknown>> | undefined> this.params?.colDef;
		return datacenterType === 'shipment' && colDef?.valueType !== undefined;
	}
}
